import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import WorkoutGraph from "./WorkoutGraph";
import {
  formatRestTimer,
  formatSet,
  getCurrentSessionSets,
  groupSetsBySession,
  REST_TIMER_MS,
} from "./workoutUtils";

const GRAPH_OPTIONS = [
  { value: "totalVolume", label: "総ボリューム" },
  { value: "maxEstimated1RM", label: "最大推定1RM" },
];

const IS_DEV_MODE = process.env.REACT_APP_DEV_MODE === "true";

function MainViewContent({
  workouts,
  isLoading,
  errorMessage,
  onCreateWorkout,
  onAddSet,
  onDeleteSet,
  onUpdateGraphType,
  copyStatus,
}) {
  const [nowTs, setNowTs] = useState(Date.now());
  const [selectorValue, setSelectorValue] = useState("");
  const [selectedWorkoutId, setSelectedWorkoutId] = useState(null);
  const [inputSet, setInputSet] = useState({ weight: "", reps: "" });
  const [uiStatus, setUiStatus] = useState("idle");
  const [customRecordedAt, setCustomRecordedAt] = useState("");
  const [timerMs, setTimerMs] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [deletingSetId, setDeletingSetId] = useState(null);
  const timerRef = useRef(null);
  const isDevMode = IS_DEV_MODE;
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(null);
  const dataListId = useId();

  const workoutMap = useMemo(() => {
    return workouts.reduce((acc, workout) => {
      acc[workout.id] = workout;
      return acc;
    }, {});
  }, [workouts]);

  useEffect(() => {
    const timer = setInterval(() => setNowTs(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selectedWorkoutId && workouts.length > 0) {
      setSelectedWorkoutId(workouts[0].id);
      setSelectorValue(workouts[0].name);
    }
    if (selectedWorkoutId && !workoutMap[selectedWorkoutId]) {
      setSelectedWorkoutId(workouts[0]?.id ?? null);
      setSelectorValue(workouts[0]?.name ?? "");
    }
  }, [selectedWorkoutId, workouts, workoutMap]);

  const selectedWorkout = selectedWorkoutId
    ? workoutMap[selectedWorkoutId]
    : null;

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsTimerRunning(false);
  }, []);

  const resetTimer = useCallback(() => {
    stopTimer();
    setTimerMs(0);
  }, [stopTimer]);

  useEffect(() => {
    return () => {
      stopTimer();
    };
  }, [stopTimer]);

  useEffect(() => {
    resetTimer();
  }, [selectedWorkoutId, resetTimer]);

  useEffect(() => {
    setSelectedHistoryIndex(null);
  }, [selectedWorkoutId]);

  const startTimer = () => {
    stopTimer();
    const start = Date.now();
    setTimerMs(0);
    setIsTimerRunning(true);
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      if (elapsed >= REST_TIMER_MS) {
        setTimerMs(REST_TIMER_MS);
        stopTimer();
        return;
      }
      setTimerMs(elapsed);
    }, 300);
  };

  const loadExistingWorkout = (workout) => {
    setSelectedWorkoutId(workout.id);
    setSelectorValue("");
  };

  const handleSelectorChange = (event) => {
    const value = event.target.value;
    setSelectorValue(value);
    const existing = workouts.find(
      (workout) => workout.name.toLowerCase() === value.trim().toLowerCase()
    );
    if (existing && existing.name === value.trim()) {
      loadExistingWorkout(existing);
    }
  };

  const handleLoadWorkout = async () => {
    const trimmed = selectorValue.trim();
    if (!trimmed) {
      return;
    }
    const existing = workouts.find(
      (workout) => workout.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (existing) {
      loadExistingWorkout(existing);
      return;
    }
    try {
      setUiStatus("creating");
      const workout = await onCreateWorkout(trimmed);
      setSelectedWorkoutId(workout.id);
      setSelectorValue("");
      setUiStatus("created");
      setTimeout(() => setUiStatus("idle"), 1500);
    } catch (error) {
      console.error("Failed to create workout", error);
      setUiStatus("create-error");
      setTimeout(() => setUiStatus("idle"), 2000);
    }
  };

  const handleSetInputChange = (event) => {
    const { name, value } = event.target;
    setInputSet((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddSet = async () => {
    if (!selectedWorkout) {
      return;
    }
    const parsedWeight =
      inputSet.weight === ""
        ? Number(latestSet?.weight)
        : Number(inputSet.weight);
    const parsedReps =
      inputSet.reps === "" ? Number(latestSet?.reps) : Number(inputSet.reps);
    const weight = Number.isFinite(parsedWeight) ? parsedWeight : NaN;
    const reps = Number.isFinite(parsedReps) ? parsedReps : NaN;
    if (
      !Number.isFinite(weight) ||
      weight <= 0 ||
      !Number.isFinite(reps) ||
      reps <= 0
    ) {
      setUiStatus("add-error");
      setTimeout(() => setUiStatus("idle"), 1200);
      return;
    }
    const recordedAtValue =
      isDevMode && customRecordedAt ? customRecordedAt : undefined;
    try {
      await onAddSet(selectedWorkout.id, {
        weight,
        reps,
        recordedAt: recordedAtValue,
      });
      setInputSet({ weight: "", reps: "" });
      startTimer();
    } catch (error) {
      console.error("Failed to add workout set", error);
      setUiStatus("commit-error");
    } finally {
      setTimeout(() => setUiStatus("idle"), 2000);
    }
  };

  const handleDeleteSet = async (setId) => {
    if (!selectedWorkout || !setId || deletingSetId) {
      return;
    }
    try {
      setDeletingSetId(setId);
      await onDeleteSet(selectedWorkout.id, setId);
    } catch (error) {
      console.error("Failed to delete workout set", error);
      setUiStatus("delete-error");
      setTimeout(() => setUiStatus("idle"), 2000);
    } finally {
      setDeletingSetId(null);
    }
  };

  const handleGraphTypeChange = async (event) => {
    if (!selectedWorkout) {
      return;
    }
    const nextType = event.target.value;
    try {
      await onUpdateGraphType(selectedWorkout.id, nextType);
    } catch (error) {
      console.error("Failed to update graph type", error);
      setUiStatus("graph-error");
      setTimeout(() => setUiStatus("idle"), 2000);
    }
  };

  const renderStatus = () => {
    if (isLoading) {
      return <p className="status-text">読み込み中...</p>;
    }
    if (copyStatus === "copied") {
      return <p className="status-text">コピーしました</p>;
    }
    if (copyStatus === "copy-error") {
      return <p className="form-error">コピーに失敗しました</p>;
    }
    if (uiStatus === "create-error") {
      return <p className="form-error">種目の作成に失敗しました</p>;
    }
    if (uiStatus === "created") {
      return <p className="status-text">種目を追加しました</p>;
    }
    if (uiStatus === "graph-error") {
      return <p className="form-error">グラフの更新に失敗しました</p>;
    }
    return null;
  };

  const addSetError = () => {
    if (uiStatus === "add-error") {
      return "重量と回数は1以上の数字で入力してください";
    }
    if (uiStatus === "commit-error") {
      return "記録の保存に失敗しました";
    }
    if (uiStatus === "delete-error") {
      return "セットの削除に失敗しました";
    }
    return "";
  };

  const groupedHistory = useMemo(
    () => (selectedWorkout ? groupSetsBySession(selectedWorkout.sets) : []),
    [selectedWorkout]
  );

  const normalizedGraphType = useMemo(() => {
    const value = selectedWorkout?.graphType;
    const allowed = GRAPH_OPTIONS.some((option) => option.value === value);
    return allowed ? value : "totalVolume";
  }, [selectedWorkout]);

  const currentSessionSets = useMemo(
    () =>
      selectedWorkout ? getCurrentSessionSets(selectedWorkout.sets, nowTs) : [],
    [selectedWorkout, nowTs]
  );

  const latestSet = currentSessionSets[0];

  useEffect(() => {
    if (
      selectedHistoryIndex !== null &&
      (selectedHistoryIndex < 0 ||
        selectedHistoryIndex >= groupedHistory.length)
    ) {
      setSelectedHistoryIndex(null);
    }
  }, [selectedHistoryIndex, groupedHistory.length]);

  const selectedHistory = useMemo(() => {
    if (selectedHistoryIndex === null) {
      return null;
    }
    return groupedHistory[selectedHistoryIndex] || null;
  }, [groupedHistory, selectedHistoryIndex]);

  const selectedHistoryLabel = useMemo(() => {
    if (!selectedHistory) {
      return "";
    }
    const date = new Date(selectedHistory.timestamp);
    if (Number.isNaN(date.getTime())) {
      return selectedHistory.timestamp;
    }
    return date.toLocaleString();
  }, [selectedHistory]);

  const handleSelectSession = useCallback((index) => {
    setSelectedHistoryIndex(index);
  }, []);

  return (
    <>
      <div className="selector-row">
        <label className="form-label selector-label">
          種目を選択 or 新規作成
          <div className="selector-input">
            <input
              className="form-input"
              list={dataListId}
              value={selectorValue}
              onChange={handleSelectorChange}
              placeholder="例: ベンチプレス"
              disabled={uiStatus === "creating"}
            />
            <datalist id={dataListId}>
              {workouts.map((workout) => (
                <option key={workout.id} value={workout.name} />
              ))}
            </datalist>
          </div>
        </label>
        <button
          className="primary-button"
          type="button"
          onClick={handleLoadWorkout}
          disabled={uiStatus === "creating"}
        >
          {uiStatus === "creating" ? "作成中..." : "決定"}
        </button>
      </div>
      {renderStatus()}
      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}

      {selectedWorkout ? (
        <section className="workout-block">
          <h2 className="workout-title">{selectedWorkout.name}</h2>
          <WorkoutGraph
            history={groupedHistory}
            graphType={normalizedGraphType}
            onSelectSession={handleSelectSession}
            selectedHistoryIndex={selectedHistoryIndex}
          />
          <label className="form-label">
            グラフの種類
            <select
              className="form-input"
              value={normalizedGraphType}
              onChange={handleGraphTypeChange}
            >
              {GRAPH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <hr className="divider" />
          <div className="input-block">
            <label className="form-label">
              重量 (kg)
              <input
                className="form-input"
                type="number"
                name="weight"
                min="0"
                value={inputSet.weight}
                placeholder={latestSet?.weight ? String(latestSet.weight) : ""}
                onChange={handleSetInputChange}
              />
            </label>
            <label className="form-label">
              回数
              <input
                className="form-input"
                type="number"
                name="reps"
                min="0"
                value={inputSet.reps}
                placeholder={latestSet?.reps ? String(latestSet.reps) : ""}
                onChange={handleSetInputChange}
              />
            </label>
            <div className="timer-display">
              <span className="timer-label">休憩タイマー</span>
              <span className="timer-value">{formatRestTimer(timerMs)}</span>
              <span className="timer-status">
                {isTimerRunning ? "計測中" : "停止中"}
              </span>
            </div>
            <button
              className="secondary-button"
              type="button"
              onClick={handleAddSet}
            >
              add set
            </button>
            {addSetError() ? (
              <p className="form-error">{addSetError()}</p>
            ) : null}
          </div>
          {isDevMode ? (
            <div className="commit-row">
              <label className="form-label dev-timestamp-label">
                日時 (dev)
                <input
                  className="form-input"
                  type="datetime-local"
                  value={customRecordedAt}
                  onChange={(event) =>
                    setCustomRecordedAt(event.target.value)
                  }
                />
              </label>
            </div>
          ) : null}
          {currentSessionSets.length > 0 ? (
            <div className="pending-set-list">
              {currentSessionSets.map((set, index) => {
                const key = set.id || set._id || set.recordedAt || index;
                return (
                  <div className="pending-set-item" key={key}>
                    <span>{set.recordedAtLabel}</span>
                    <span className="pending-set-actions">
                      <span>{formatSet(set)}</span>
                      <button
                        className="link-button delete-set-button"
                        type="button"
                        onClick={() => handleDeleteSet(set.id)}
                        disabled={deletingSetId === set.id}
                        aria-label={`${formatSet(set)} を削除`}
                      >
                        {deletingSetId === set.id ? "削除中..." : "削除"}
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}
          {selectedHistory ? (
            <div className="selected-session">
              <div className="selected-session-header">
                <span className="selected-session-title">
                  選択中のセッション
                </span>
                <span className="selected-session-time">
                  {selectedHistoryLabel}
                </span>
              </div>
              <div className="pending-set-list">
                {selectedHistory.sets.map((set, index) => {
                  const key = set.id || set._id || set.recordedAt || index;
                  return (
                    <div className="pending-set-item" key={key}>
                      <span>{formatSet(set)}</span>
                      <span>{new Date(set.recordedAt).toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </section>
      ) : (
        <p className="status-text">種目を選択または新規作成してください。</p>
      )}
    </>
  );
}

export default MainViewContent;
